import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  AlertTriangle,
  CheckCircle,
  Eye,
  EyeOff,
  Loader2,
  Lock,
  Shield,
} from 'lucide-react';
import useToast from '../hooks/useToast';
import { SkeletonBlock } from '../components/Skeleton';
import { API_URL } from '../config';
import { haptic } from '../utils/vibration';
import FloatingField from '../components/FloatingField';

const MIN_PASSWORD_LENGTH = 7;

const postResetApi = async (path, body) => {
  const response = await fetch(`${API_URL}/api${path}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(body),
  });

  let data = {};
  try {
    data = await response.json();
  } catch (error) {
    data = {};
  }

  if (!response.ok) {
    throw new Error(data?.detail || data?.message || `Ошибка ${response.status}`);
  }

  return data;
};

const formatDate = (value) => {
  if (!value) return '';
  const date = new Date(value);
  return date.toLocaleString('ru-RU', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    timeZone: 'Europe/Moscow',
  });
};

const ResetPassword = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const token = useMemo(() => new URLSearchParams(location.search).get('token') || '', [location.search]);

  const [validating, setValidating] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [tokenInfo, setTokenInfo] = useState(null);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [completed, setCompleted] = useState(false);
  const { showToast } = useToast();

  useEffect(() => {
    let cancelled = false;

    const validateToken = async () => {
      if (!token) {
        setError('Ссылка для сброса пароля не содержит токен.');
        setValidating(false);
        return;
      }

      setValidating(true);
      setError('');

      try {
        const data = await postResetApi('/password-reset/validate', { token });
        if (cancelled) return;
        setTokenInfo(data);
      } catch (err) {
        if (cancelled) return;
        setError(err.message || 'Не удалось проверить ссылку.');
      } finally {
        if (!cancelled) {
          setValidating(false);
        }
      }
    };

    validateToken();
    return () => {
      cancelled = true;
    };
  }, [token]);



  const handleSubmit = async (event) => {
    event.preventDefault();
    setError('');

    if (!password) {
      setError('Введите новый пароль.');
      showToast('Введите новый пароль.', 'error');
      return;
    }
    if (password.length < MIN_PASSWORD_LENGTH) {
      const msg = `Пароль должен содержать минимум ${MIN_PASSWORD_LENGTH} символов.`;
      setError(msg);
      showToast(msg, 'error');
      return;
    }
    if (password !== confirmPassword) {
      setError('Пароли не совпадают.');
      showToast('Пароли не совпадают.', 'error');
      return;
    }

    setSubmitting(true);
    try {
      const data = await postResetApi('/password-reset/complete', {
        token,
        new_password: password,
      });
      if (data?.login) {
        localStorage.setItem('last_login', data.login);
      }
      setTokenInfo((prev) => ({
        ...(prev || {}),
        login: data?.login || prev?.login || '',
        user_status: data?.user_status || prev?.user_status || '',
      }));
      setCompleted(true);
      setPassword('');
      setConfirmPassword('');
      showToast('Новый пароль сохранён.');
    } catch (err) {
      const msg = err.message || 'Не удалось сохранить новый пароль.';
      setError(msg);
      showToast(msg, 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const openAuth = () => {
    navigate('/auth', { replace: true });
  };

  return (
    <div className="min-h-screen bg-gray-50 px-4 py-8 dark:bg-slate-900">
      <div className="mx-auto max-w-md">
        <div className="overflow-hidden rounded-3xl border border-gray-200 bg-white shadow-sm dark:border-gray-700 dark:bg-gray-900">
          <div className="border-b border-gray-100 bg-gradient-to-r from-blue-600 to-cyan-500 px-6 py-6 text-white dark:border-gray-800">
            <div className="flex items-center gap-3">
              <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-white/15">
                <Shield className="h-6 w-6" />
              </div>
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.2em] text-blue-100">Безопасность</p>
                <h1 className="text-xl font-semibold">Установка нового пароля</h1>
              </div>
            </div>
          </div>

          <div className="space-y-5 px-6 py-6">
            {validating ? (
              <div className="space-y-4 py-2" role="status" aria-label="Проверка ссылки для сброса пароля">
                <SkeletonBlock className="h-4 w-2/3" />
                <SkeletonBlock className="h-12 w-full rounded-xl" />
                <SkeletonBlock className="h-12 w-full rounded-xl" />
                <SkeletonBlock className="h-11 w-full rounded-xl" />
              </div>
            ) : error ? (
              <div className="space-y-4">
                <div className="rounded-2xl border border-red-200 bg-red-50 p-4 dark:border-red-900/40 dark:bg-red-900/20">
                  <div className="flex items-start gap-3">
                    <AlertTriangle className="mt-0.5 h-5 w-5 text-red-600 dark:text-red-300" />
                    <div>
                      <h2 className="text-sm font-semibold text-red-800 dark:text-red-200">Ссылка недоступна</h2>
                      <p className="mt-1 text-sm text-red-700 dark:text-red-300">{error}</p>
                    </div>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={openAuth}
                  className="w-full rounded-2xl bg-blue-600 px-4 py-3 text-sm font-medium text-white transition-colors hover:bg-blue-700"
                >
                  К экрану входа
                </button>
              </div>
            ) : completed ? (
              <div className="space-y-4">
                <div className="rounded-2xl border border-green-200 bg-green-50 p-4 dark:border-green-900/40 dark:bg-green-900/20">
                  <div className="flex items-start gap-3">
                    <CheckCircle className="mt-0.5 h-5 w-5 text-green-600 dark:text-green-300" />
                    <div>
                      <h2 className="text-sm font-semibold text-green-800 dark:text-green-200">Пароль обновлён</h2>
                      <p className="mt-1 text-sm text-green-700 dark:text-green-300">
                        {tokenInfo?.user_status === 'blocked'
                          ? 'Новый пароль сохранён, но аккаунт пока заблокирован. Для входа нужно дождаться разблокировки администратором.'
                          : 'Новый пароль сохранён. Теперь можно войти в систему с новым паролем.'}
                      </p>
                    </div>
                  </div>
                </div>
                {tokenInfo?.login && (
                  <div className="rounded-2xl border border-gray-200 bg-gray-50 px-4 py-3 text-sm text-gray-700 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-200">
                    Логин: <strong>{tokenInfo.login}</strong>
                  </div>
                )}
                <button
                  type="button"
                  onClick={openAuth}
                  className="w-full rounded-2xl bg-blue-600 px-4 py-3 text-sm font-medium text-white transition-colors hover:bg-blue-700"
                >
                  Перейти ко входу
                </button>
              </div>
            ) : (
              <>
                <div className="rounded-2xl border border-blue-100 bg-blue-50 p-4 text-sm text-blue-900 dark:border-blue-900/40 dark:bg-blue-900/20 dark:text-blue-100">
                  <p>
                    Для пользователя <strong>{tokenInfo?.login || 'без логина'}</strong> создана одноразовая ссылка.
                  </p>
                  {tokenInfo?.expires_at && (
                    <p className="mt-1 text-blue-700 dark:text-blue-200">
                      Действует до {formatDate(tokenInfo.expires_at)}.
                    </p>
                  )}
                </div>

                {tokenInfo?.user_status === 'blocked' && (
                  <div className="rounded-2xl border border-yellow-200 bg-yellow-50 p-4 text-sm text-yellow-900 dark:border-yellow-900/40 dark:bg-yellow-900/20 dark:text-yellow-100">
                    Аккаунт сейчас заблокирован. Пароль можно обновить заранее, но войти получится только после разблокировки.
                  </div>
                )}

                <form className="space-y-4" onSubmit={handleSubmit}>
                  <div>
                    <FloatingField label="Новый пароль">
                      <div className="relative">
                      <input
                        type={showPassword ? 'text' : 'password'}
                        value={password}
                        onChange={(event) => setPassword(event.target.value)}
                        className="w-full rounded-2xl border border-gray-300 bg-white px-4 py-3 pr-20 text-sm text-gray-900 outline-none transition-colors focus:border-blue-500 focus:ring-2 focus:ring-blue-500/30 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100"
                        placeholder={`Минимум ${MIN_PASSWORD_LENGTH} символов`}
                        autoComplete="new-password"
                        disabled={submitting}
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword((prev) => !prev)}
                        className="absolute right-10 top-1/2 -translate-y-1/2 text-gray-400 transition-colors hover:text-gray-600 dark:hover:text-gray-200"
                        disabled={submitting}
                      >
                        {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                      </button>
                      <Lock className="absolute right-3 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400" />
                      </div>
                    </FloatingField>
                  </div>

                  <div>
                    <FloatingField label="Подтверждение нового пароля">
                      <input
                        type={showPassword ? 'text' : 'password'}
                        value={confirmPassword}
                        onChange={(event) => setConfirmPassword(event.target.value)}
                        className="w-full rounded-2xl border border-gray-300 bg-white px-4 py-3 text-sm text-gray-900 outline-none transition-colors focus:border-blue-500 focus:ring-2 focus:ring-blue-500/30 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100"
                        placeholder="Повторите пароль"
                        autoComplete="new-password"
                        disabled={submitting}
                      />
                    </FloatingField>
                  </div>

                  {error && (
                    <div className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 dark:border-red-900/40 dark:bg-red-900/20 dark:text-red-200">
                      {error}
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={submitting}
                    className="inline-flex w-full items-center justify-center gap-2 rounded-2xl bg-blue-600 px-4 py-3 text-sm font-medium text-white transition-colors hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Shield className="h-4 w-4" />}
                    <span>{submitting ? 'Сохраняем пароль...' : 'Сохранить новый пароль'}</span>
                  </button>
                </form>
              </>
            )}
          </div>
        </div>
      </div>

    </div>
  );
};

export default ResetPassword;
